// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import { GlobalContext } from 'context/GlobalContextProvider'

// Img - the device shot from the AIR-1000 product page
import Air1000Image from 'public/img/products/Air1000/air1000_in_industry.jpg'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBodySmall } from '@/assets/theme/fonts'

export default function DeviceSection() {
  const { t } = useTranslation('solutionsIotAirQualityMonitoring')
  const { screenDimensions } = useContext(GlobalContext)

  const stacked = screenDimensions.width < breakpoints.desktopToMobile

  const getContent = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: stacked ? '1fr' : '1fr 1fr',
        gap: stacked ? '2rem' : '3rem',
        alignItems: 'center',
        width: '100%'
      }}
    >
      <Box>
        <SectionHeader
          eyebrow={t('device.eyebrow')}
          title={t('device.title')}
        />

        {[t('device.p1'), t('device.p2'), t('device.p3'), t('device.p4')].map(
          paragraph => (
            <Typography
              key={paragraph}
              component="p"
              sx={{
                fontSize: getFontBodySmall(),
                lineHeight: 1.7,
                color: themeColors.gray.defaultText,
                marginBottom: '0.9rem',
                '&:last-of-type': { marginBottom: 0 }
              }}
            >
              {paragraph}
            </Typography>
          )
        )}
      </Box>

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '4 / 3',
          borderRadius: '10px',
          overflow: 'hidden',
          order: stacked ? -1 : 0
        }}
      >
        <Image
          src={Air1000Image}
          alt="AIR-1000 air quality monitoring device installed in an industrial environment"
          fill
          sizes="(max-width: 990px) 100vw, 48vw"
          // Cropped from the top: the bottom of the shot always stays visible.
          style={{ objectFit: 'cover', objectPosition: 'center bottom' }}
        />
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.kroemtech.background}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
